import { algoliasearch } from 'algoliasearch';
import { Card, CardBody, Col, Row } from 'react-bootstrap';
import { Configure, InstantSearch, useInfiniteHits, useInstantSearch } from 'react-instantsearch';
import { faSearch } from '@awesome.me/kit-3f20e419a7/icons/classic/solid';
import { t } from 'react-switch-lang';
import { useEffect, useState } from 'react';
import Image from 'next/image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DISPATCHERS, usePaymentProcessContext } from '../../context/PaymentProcessContext';
import CustomSearchBox from './CustomSearchBox';
import styles from '../../styles/PartnerSearch.module.scss';
import Button from '../Button';
import { events } from '../../utils/Amplitude';
import { getLanguageString, getPayLinkPrefix } from '../../utils/Helpers';
import SectionTitle from '../SectionTitle';
import { setScreenReaderAlert } from '../../utils/Accessibility';
import Link from '../Link';
import CardWithLoadingIndicator from '../CardWithLoadingIndicator';

function NoResults() {
  return (
    <div className={styles.noResultsTitle}>
      <FontAwesomeIcon icon={faSearch} />
      <p>{t('PaymentProcess_Search_NoResultsTitle')}</p>
    </div>
  );
}

function CustomHits({ landingPageKey, onSelect }) {
  // eslint-disable-next-line max-len
  /** @type {import('instantsearch.js/es/connectors/infinite-hits/connectInfiniteHits').InfiniteHitsRenderState<import('../../context/PaymentProcessContext').AlgoliaHit>} */
  const { items, isLastPage, showMore, results } = useInfiniteHits();
  const { dispatchPaymentProcess } = usePaymentProcessContext();
  const { indexUiState, status } = useInstantSearch();
  const [showNoResults, setShowNoResults] = useState(false);

  useEffect(() => {
    setShowNoResults(false);
    if (status !== 'idle' || !indexUiState.query) return;

    if (results.nbHits === 0) {
      setScreenReaderAlert(t('PaymentProcess_Search_NoResultsTitle'));
      setShowNoResults(true);
    } else if (results.nbHits > 0) {
      setScreenReaderAlert(t('PaymentProcess_Search_ResultsFound'));
    }
  }, [indexUiState.query, results.nbHits, status]);

  const [leftColumn, rightColumn] = items?.reduce(
    (acc, hit, index) => {
      acc[index % 2].push(hit); // Push evens to left column, odds to right column
      return acc;
    },
    [[], []] // First array for left column, second for right column
  ) || [[], []];

  return (
    <div>
      <div className={styles.hitContainerWeb}>
        <div className={styles.hitColumn}>
          {leftColumn.map((hit) => (
            <Link
              className={styles.hitButton}
              ampEvent={events.USER_SELECTED_ACCOUNT_TYPE_PARTNER}
              key={hit.objectID}
              aria-label={t('PaymentRecipient_LinkDescription').replace(
                'xRecipient',
                getLanguageString(hit.DisplayNameEn, hit.DisplayNameFr)
              )}
              ampEventProps={{
                Partner: hit.DisplayNameEn,
                Partner_seq: hit.objectID,
                RemittanceType: hit.RemittanceType,
              }}
              href={`${
                landingPageKey ? `/lp/${landingPageKey}` : ''
              }/${getPayLinkPrefix()}/${hit.objectID}`}
              onClick={() => {
                sessionStorage.removeItem('paymentProcess');
                dispatchPaymentProcess(DISPATCHERS.SELECT_ALGOLIA_OBJECT(hit));
                onSelect();
              }}
            >
              <Card className={styles.hitItem}>
                <CardBody className="p-2">
                  {hit?.LogoUrl && !landingPageKey && (
                  <div className={styles.hitImageContainer}>
                    <Image
                      src={hit?.LogoUrl}
                      alt={getLanguageString(hit?.LogoAltEn, hit?.LogoAltFr)}
                      style={{ objectFit: 'contain' }}
                      fill
                      sizes="(max-width: 768px) 100vw, 50vw"
                      height={0}
                      width={0}
                      unoptimized // allow any remote urls
                    />
                  </div>
                  )}
                  <h3 className={styles.hitTitle}>
                    {getLanguageString(hit.DisplayNameEn, hit.DisplayNameFr)}
                  </h3>
                  <div className={styles.hitDescription}>
                    {getLanguageString(hit.DisplayDescriptionEn, hit.DisplayDescriptionFr)}
                  </div>
                </CardBody>
              </Card>
            </Link>
          ))}
        </div>

        <div className={styles.hitColumn}>
          {rightColumn.map((hit) => (
            <Link
              className={styles.hitButton}
              ampEvent={events.USER_SELECTED_ACCOUNT_TYPE_PARTNER}
              key={hit.objectID}
              aria-label={t('PaymentRecipient_LinkDescription').replace(
                'xRecipient',
                getLanguageString(hit.DisplayNameEn, hit.DisplayNameFr)
              )}
              ampEventProps={{
                Partner: hit.DisplayNameEn,
                Partner_seq: hit.objectID,
                RemittanceType: hit.RemittanceType,
              }}
              href={`${
                landingPageKey ? `/lp/${landingPageKey}` : ''
              }/${getPayLinkPrefix()}/${hit.objectID}`}
              onClick={() => {
                sessionStorage.removeItem('paymentProcess');
                dispatchPaymentProcess(DISPATCHERS.SELECT_ALGOLIA_OBJECT(hit));
                onSelect();
              }}
            >
              <Card className={styles.hitItem}>
                <CardBody className="p-2">
                  {hit?.LogoUrl && !landingPageKey && (
                  <div className={styles.hitImageContainer}>
                    <Image
                      src={hit?.LogoUrl}
                      alt={getLanguageString(hit?.LogoAltEn, hit?.LogoAltFr)}
                      style={{ objectFit: 'contain' }}
                      fill
                      sizes="(max-width: 768px) 100vw, 50vw"
                      height={0}
                      width={0}
                      unoptimized // allow any remote urls
                    />
                  </div>
                  )}
                  <h3 className={styles.hitTitle}>
                    {getLanguageString(hit.DisplayNameEn, hit.DisplayNameFr)}
                  </h3>
                  <div className={styles.hitDescription}>
                    {getLanguageString(hit.DisplayDescriptionEn, hit.DisplayDescriptionFr)}
                  </div>
                </CardBody>
              </Card>
            </Link>
          ))}
        </div>
      </div>
      <div className={styles.hitContainerMobile}>
        <div className={styles.hitColumn}>
          {items.map((hit) => (
            <Link
              className={styles.hitButton}
              ampEvent={events.USER_SELECTED_ACCOUNT_TYPE_PARTNER}
              key={hit.objectID}
              aria-label={t('PaymentRecipient_LinkDescription').replace(
                'xRecipient',
                getLanguageString(hit.DisplayNameEn, hit.DisplayNameFr)
              )}
              ampEventProps={{
                Partner: hit.DisplayNameEn,
                Partner_seq: hit.objectID,
                RemittanceType: hit.RemittanceType,
              }}
              href={`${
                landingPageKey ? `/lp/${landingPageKey}` : ''
              }/${getPayLinkPrefix()}/${hit.objectID}`}
              onClick={() => {
                sessionStorage.removeItem('paymentProcess');
                dispatchPaymentProcess(DISPATCHERS.SELECT_ALGOLIA_OBJECT(hit));
                onSelect();
              }}
            >
              <Card className={styles.hitItem}>
                <CardBody className="p-2">
                  {hit?.LogoUrl && !landingPageKey && (
                  <div className={styles.hitImageContainer}>
                    <Image
                      src={hit?.LogoUrl}
                      alt={getLanguageString(hit?.LogoAltEn, hit?.LogoAltFr)}
                      style={{ objectFit: 'contain' }}
                      fill
                      sizes="(max-width: 768px) 100vw, 50vw"
                      height={0}
                      width={0}
                      unoptimized // allow any remote urls
                    />
                  </div>
                  )}
                  <h3 className={styles.hitTitle}>
                    {getLanguageString(hit.DisplayNameEn, hit.DisplayNameFr)}
                  </h3>
                  <div className={styles.hitDescription}>
                    {getLanguageString(hit.DisplayDescriptionEn, hit.DisplayDescriptionFr)}
                  </div>
                </CardBody>
              </Card>
            </Link>
          ))}
        </div>
      </div>
      <div className={styles.showMoreButtonContainer}>
        {
          showNoResults && <NoResults />
        }
        {!isLastPage &&
          (
            <Button
              onClick={showMore}
              ampEvent={events.USER_CLICKED_SHOW_MORE_PARTNERS}
              ariaLabel={t('Search_ShowMore_ButtonDescription')}
            >
              {t('PaymentProcess_Search_ShowMore')}
            </Button>
          )}
      </div>
    </div>
  );
}

const algoliaClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY
);

/**
 * Wrapper function that modifies the search method of an Algolia client to ignore empty queries.
 * @param {import('algoliasearch').Algoliasearch} client
 * @returns {import('algoliasearch').Algoliasearch}
 */
export function ignoreEmptyQuery(client) {
  return {
    ...client,
    search(requests) {
      if (requests.every(({ params }) => !params.query)) {
        return Promise.resolve({
          results: requests.map(() => ({
            hits: [],
            nbHits: 0,
            nbPages: 0,
            page: 0,
            processingTimeMS: 0,
            hitsPerPage: 0,
            exhaustiveNbHits: false,
            query: '',
            params: '',
          })),
        });
      }

      return client.search(requests);
    },
  };
}
const searchClient = ignoreEmptyQuery(algoliaClient);

/**
 * @param {object} props
 * @param {string} [props.landingPageKey]
 * @param {string} [props.initialQuery]
 * @param {boolean} [props.hideSearchBox] to prevent rendering searchBox component within
 * `getServerSideProps`, since it uses `useRouter` and throws an {@link https://nextjs.org/docs/messages/next-router-not-mounted|error}
 * @param {import('algoliasearch').Algoliasearch} [props.serverSideSearchClient]
 */
export default function PartnerSearch({
  landingPageKey = null,
  initialQuery = '',
  hideSearchBox = false,
  serverSideSearchClient = undefined,
}) {
  const [loading, setLoading] = useState(false);
  const filterString = landingPageKey ? `Status:A AND LandingPageKey:${landingPageKey}` : 'Status:A';
  const indexName = process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME;
  let client = landingPageKey ? algoliaClient : searchClient;
  if (serverSideSearchClient) client = serverSideSearchClient;

  if (loading) return <CardWithLoadingIndicator />;

  return (
    <Card className={styles.searchContainer}>
      <InstantSearch
        searchClient={client}
        indexName={indexName}
        future={{
          preserveSharedStateOnUnmount: false,
        }}
        initialUiState={hideSearchBox ? undefined : {
          [indexName]: {
            query: initialQuery,
          },
        }}
      >
        <Configure
          filters={filterString}
          hitsPerPage={landingPageKey ? 50 : 20}
          query={initialQuery}
        />
        <CardBody>
          <Row>
            <Col md={12} className="mb-3 d-flex flex-column justify-content-md-center">
              <SectionTitle
                title={t('PaymentProcess_Search_Prompt')}
                subtitle={!landingPageKey ? t('PaymentProcess_Search_SubTitle') : null}
                lgSubtitle
              />
              {!hideSearchBox && <CustomSearchBox initialQuery={initialQuery} />}
            </Col>
          </Row>
          <CustomHits landingPageKey={landingPageKey} onSelect={() => setLoading(true)} />
        </CardBody>
      </InstantSearch>
    </Card>
  );
}
